/**
 * Season-year helpers for ESPN / schedule APIs.
 *
 * Football (NFL / CFB): named by the year the season starts
 *   (2026 CFB = Aug 2026 → Jan 2027).
 * NBA / NHL: named by the year the season starts
 *   (2026-27 = Oct 2026 → Jun 2027).
 * MLB: named by the calendar year of the season.
 *
 * These return the season most useful for "upcoming games" / trip planning.
 */

/**
 * @param {Date} [date]
 * @returns {number}
 */
export function getFootballSeasonYear(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 1–12
  // January: still previous season (bowls / NFL playoffs)
  if (month === 1) return year - 1;
  // Feb–Dec: upcoming or in-progress season for this calendar year
  return year;
}

/**
 * @param {Date} [date]
 * @returns {number}
 */
export function getBasketballHockeySeasonYear(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  // Jan–Jun: season that started the previous fall
  if (month <= 6) return year - 1;
  // Jul–Dec: upcoming / in-progress season starting this fall
  return year;
}

/**
 * @param {Date} [date]
 * @returns {number}
 */
export function getBaseballSeasonYear(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  // Nov–Dec: prior season over; next year's schedule is what travelers need
  if (month >= 11) return year + 1;
  return year;
}

/**
 * @param {'nfl'|'college'|'nba'|'nhl'|'mlb'|'cbb'|string} league
 * @param {Date} [date]
 * @returns {number}
 */
export function getSeasonYearForLeague(league, date = new Date()) {
  switch (String(league || '').toLowerCase()) {
    case 'nfl':
    case 'college':
    case 'cfb':
      return getFootballSeasonYear(date);
    case 'nba':
    case 'nhl':
    case 'cbb':
      return getBasketballHockeySeasonYear(date);
    case 'mlb':
      return getBaseballSeasonYear(date);
    default:
      return date.getFullYear();
  }
}

/**
 * Human-readable season label for prompts / UI.
 * @param {'NFL'|'MLB'|'NBA'|'NHL'|string} teamType
 * @param {Date} [date]
 * @returns {string}
 */
export function getSeasonLabel(teamType, date = new Date()) {
  const type = String(teamType || '').toUpperCase();
  if (type === 'NFL') {
    const y = getFootballSeasonYear(date);
    return `${y} NFL season`;
  }
  if (type === 'MLB') {
    return `${getBaseballSeasonYear(date)} MLB season`;
  }
  if (type === 'NBA') {
    const y = getBasketballHockeySeasonYear(date);
    return `${y}-${String(y + 1).slice(-2)} NBA season`;
  }
  if (type === 'NHL') {
    const y = getBasketballHockeySeasonYear(date);
    return `${y}-${String(y + 1).slice(-2)} NHL season`;
  }
  const y = getFootballSeasonYear(date);
  return `${y} college football season`;
}
